import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Worksheet {
  id: string;
  title: string;
  description: string;
  topic: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  pages: number;
  thumbnail: string;
}

const worksheets: Worksheet[] = [
  // Real Number System & Properties
  {
    id: 'g9-real-numbers-1',
    title: 'Classifying Real Numbers',
    description: 'Practice identifying natural, whole, integer, rational, and irrational numbers',
    topic: 'Real Numbers',
    difficulty: 'Easy',
    pages: 3,
    thumbnail: 'https://public.readdy.ai/ai/img_res/ef9cb9d73862dad20a5fc1b1ce716245.jpg'
  },
  {
    id: 'g9-real-numbers-2',
    title: 'Order of Operations (PEMDAS)',
    description: 'Master complex expressions using parentheses, exponents, multiplication, division, addition, subtraction',
    topic: 'Real Numbers',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=colorful%20mathematical%20operations%20symbols%20arranged%20in%20order%20PEMDAS%20with%20parentheses%20exponents%20multiplication%20division%20addition%20subtraction%20on%20white%20background%20educational%20poster%20style&width=400&height=300&seq=g9rn2&orientation=landscape'
  },
  {
    id: 'g9-real-numbers-3',
    title: 'Properties of Real Numbers',
    description: 'Apply commutative, associative, distributive, identity, and inverse properties',
    topic: 'Real Numbers',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=mathematical%20property%20equations%20showing%20commutative%20associative%20distributive%20patterns%20with%20colorful%20brackets%20and%20numbers%20on%20clean%20white%20background%20educational%20diagram%20style&width=400&height=300&seq=g9rn3&orientation=landscape'
  },
  
  // Linear Equations & Inequalities
  {
    id: 'g9-linear-1',
    title: 'One-Step & Two-Step Equations',
    description: 'Solve basic linear equations using inverse operations',
    topic: 'Linear Equations',
    difficulty: 'Easy',
    pages: 3,
    thumbnail: 'https://readdy.ai/api/search-image?query=simple%20linear%20equation%20with%20balance%20scale%20showing%20algebraic%20solving%20steps%20with%20arrows%20and%20operations%20on%20white%20background%20educational%20illustration%20clean%20design&width=400&height=300&seq=g9le1&orientation=landscape'
  },
  {
    id: 'g9-linear-2',
    title: 'Multi-Step Equations',
    description: 'Solve equations requiring multiple operations and combining like terms',
    topic: 'Linear Equations',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=complex%20multi%20step%20algebraic%20equation%20with%20multiple%20terms%20being%20simplified%20step%20by%20step%20with%20colorful%20arrows%20showing%20process%20on%20white%20background%20educational%20style&width=400&height=300&seq=g9le2&orientation=landscape'
  },
  {
    id: 'g9-linear-3',
    title: 'Equations with Variables on Both Sides',
    description: 'Solve equations where the variable appears on both sides',
    topic: 'Linear Equations',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=algebraic%20equation%20with%20variables%20on%20left%20and%20right%20sides%20with%20arrows%20showing%20moving%20terms%20across%20equals%20sign%20on%20white%20background%20educational%20diagram&width=400&height=300&seq=g9le3&orientation=landscape'
  },
  {
    id: 'g9-linear-4',
    title: 'Literal Equations & Formulas',
    description: 'Solve for a specific variable in formulas and literal equations',
    topic: 'Linear Equations',
    difficulty: 'Hard',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=mathematical%20formulas%20with%20multiple%20variables%20being%20rearranged%20and%20isolated%20with%20colorful%20highlighting%20on%20white%20background%20educational%20poster%20style&width=400&height=300&seq=g9le4&orientation=landscape'
  },
  {
    id: 'g9-linear-5',
    title: 'One-Variable Inequalities',
    description: 'Solve and graph linear inequalities on number lines',
    topic: 'Linear Equations',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=number%20line%20with%20inequality%20solution%20showing%20open%20and%20closed%20circles%20with%20shaded%20regions%20and%20arrows%20on%20white%20background%20educational%20math%20diagram&width=400&height=300&seq=g9le5&orientation=landscape'
  },
  {
    id: 'g9-linear-6',
    title: 'Compound Inequalities',
    description: 'Solve AND/OR compound inequalities and graph solutions',
    topic: 'Linear Equations',
    difficulty: 'Hard',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=two%20number%20lines%20showing%20compound%20inequality%20solutions%20with%20overlapping%20shaded%20regions%20and%20union%20intersection%20symbols%20on%20white%20background%20educational%20style&width=400&height=300&seq=g9le6&orientation=landscape'
  },
  {
    id: 'g9-linear-7',
    title: 'Absolute Value Equations & Inequalities',
    description: 'Solve equations and inequalities involving absolute value',
    topic: 'Linear Equations',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=absolute%20value%20symbols%20with%20split%20cases%20showing%20positive%20and%20negative%20solutions%20on%20number%20line%20with%20colorful%20arrows%20on%20white%20background%20educational%20diagram&width=400&height=300&seq=g9le7&orientation=landscape'
  },

  // Functions & Relations
  {
    id: 'g9-functions-1',
    title: 'Relations & Functions Basics',
    description: 'Identify functions using mapping diagrams, tables, and vertical line test',
    topic: 'Functions',
    difficulty: 'Easy',
    pages: 3,
    thumbnail: 'https://readdy.ai/api/search-image?query=mapping%20diagram%20showing%20domain%20and%20range%20with%20arrows%20connecting%20input%20output%20values%20and%20vertical%20line%20test%20on%20white%20background%20educational%20illustration&width=400&height=300&seq=g9fn1&orientation=landscape'
  },
  {
    id: 'g9-functions-2',
    title: 'Domain & Range',
    description: 'Find domain and range from graphs, tables, and equations',
    topic: 'Functions',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=coordinate%20plane%20with%20function%20graph%20showing%20domain%20on%20x%20axis%20and%20range%20on%20y%20axis%20with%20colorful%20brackets%20and%20labels%20on%20white%20background%20educational%20style&width=400&height=300&seq=g9fn2&orientation=landscape'
  },
  {
    id: 'g9-functions-3',
    title: 'Function Notation & Evaluation',
    description: 'Evaluate functions using f(x) notation and substitute values',
    topic: 'Functions',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=function%20notation%20f%20of%20x%20with%20substitution%20examples%20showing%20input%20values%20being%20plugged%20into%20equations%20with%20colorful%20arrows%20on%20white%20background%20educational%20diagram&width=400&height=300&seq=g9fn3&orientation=landscape'
  },
  {
    id: 'g9-functions-4',
    title: 'Slope & Rate of Change',
    description: 'Calculate slope from two points, graphs, and tables',
    topic: 'Functions',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=coordinate%20plane%20with%20line%20showing%20rise%20over%20run%20slope%20calculation%20with%20two%20points%20marked%20and%20colorful%20triangles%20on%20white%20background%20educational%20math%20diagram&width=400&height=300&seq=g9fn4&orientation=landscape'
  },
  {
    id: 'g9-functions-5',
    title: 'Graphing Linear Functions',
    description: 'Graph lines using slope-intercept form, point-slope form, and standard form',
    topic: 'Functions',
    difficulty: 'Medium',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=coordinate%20grid%20with%20multiple%20linear%20functions%20graphed%20in%20different%20colors%20showing%20y%20intercepts%20and%20slopes%20on%20white%20background%20educational%20graph%20paper%20style&width=400&height=300&seq=g9fn5&orientation=landscape'
  },
  {
    id: 'g9-functions-6',
    title: 'Parallel & Perpendicular Lines',
    description: 'Write equations of parallel and perpendicular lines',
    topic: 'Functions',
    difficulty: 'Hard',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=coordinate%20plane%20showing%20parallel%20lines%20with%20same%20slope%20and%20perpendicular%20lines%20intersecting%20at%20right%20angles%20with%20colorful%20labels%20on%20white%20background%20educational%20diagram&width=400&height=300&seq=g9fn6&orientation=landscape'
  },

  // Systems of Equations
  {
    id: 'g9-systems-1',
    title: 'Solving Systems by Graphing',
    description: 'Find solutions by graphing two linear equations',
    topic: 'Systems',
    difficulty: 'Easy',
    pages: 3,
    thumbnail: 'https://readdy.ai/api/search-image?query=coordinate%20plane%20with%20two%20intersecting%20lines%20meeting%20at%20solution%20point%20marked%20with%20colorful%20dot%20and%20labels%20on%20white%20background%20educational%20graph%20style&width=400&height=300&seq=g9sy1&orientation=landscape'
  },
  {
    id: 'g9-systems-2',
    title: 'Solving Systems by Substitution',
    description: 'Use substitution method to solve systems algebraically',
    topic: 'Systems',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=algebraic%20substitution%20method%20showing%20one%20equation%20being%20plugged%20into%20another%20with%20colorful%20arrows%20and%20steps%20on%20white%20background%20educational%20illustration&width=400&height=300&seq=g9sy2&orientation=landscape'
  },
  {
    id: 'g9-systems-3',
    title: 'Solving Systems by Elimination',
    description: 'Use elimination method to solve systems by adding/subtracting equations',
    topic: 'Systems',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=two%20equations%20stacked%20vertically%20with%20terms%20being%20eliminated%20through%20addition%20with%20colorful%20highlighting%20and%20arrows%20on%20white%20background%20educational%20diagram&width=400&height=300&seq=g9sy3&orientation=landscape'
  },
  {
    id: 'g9-systems-4',
    title: 'Special Systems (No Solution & Infinite Solutions)',
    description: 'Identify inconsistent and dependent systems',
    topic: 'Systems',
    difficulty: 'Hard',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=coordinate%20plane%20showing%20parallel%20lines%20for%20no%20solution%20and%20overlapping%20lines%20for%20infinite%20solutions%20with%20colorful%20labels%20on%20white%20background%20educational%20graph&width=400&height=300&seq=g9sy4&orientation=landscape'
  },

  // Polynomials & Factoring
  {
    id: 'g9-poly-1',
    title: 'Adding & Subtracting Polynomials',
    description: 'Combine like terms in polynomial expressions',
    topic: 'Polynomials',
    difficulty: 'Easy',
    pages: 3,
    thumbnail: 'https://readdy.ai/api/search-image?query=polynomial%20expressions%20with%20like%20terms%20highlighted%20in%20matching%20colors%20being%20combined%20with%20arrows%20on%20white%20background%20educational%20algebra%20illustration&width=400&height=300&seq=g9po1&orientation=landscape'
  },
  {
    id: 'g9-poly-2',
    title: 'Multiplying Polynomials',
    description: 'Multiply monomials, binomials, and polynomials using distributive property',
    topic: 'Polynomials',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=polynomial%20multiplication%20showing%20distributive%20property%20with%20arrows%20connecting%20terms%20and%20colorful%20area%20model%20on%20white%20background%20educational%20diagram&width=400&height=300&seq=g9po2&orientation=landscape'
  },
  {
    id: 'g9-poly-3',
    title: 'FOIL Method & Special Products',
    description: 'Multiply binomials using FOIL and recognize special patterns',
    topic: 'Polynomials',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=FOIL%20method%20diagram%20showing%20First%20Outer%20Inner%20Last%20terms%20with%20colorful%20arrows%20connecting%20binomial%20factors%20on%20white%20background%20educational%20algebra%20poster&width=400&height=300&seq=g9po3&orientation=landscape'
  },
  {
    id: 'g9-poly-4',
    title: 'Factoring Greatest Common Factor (GCF)',
    description: 'Factor out the GCF from polynomial expressions',
    topic: 'Polynomials',
    difficulty: 'Easy',
    pages: 3,
    thumbnail: 'https://readdy.ai/api/search-image?query=polynomial%20expression%20with%20greatest%20common%20factor%20being%20pulled%20out%20with%20colorful%20highlighting%20and%20brackets%20on%20white%20background%20educational%20factoring%20diagram&width=400&height=300&seq=g9po4&orientation=landscape'
  },
  {
    id: 'g9-poly-5',
    title: 'Factoring Trinomials (x² + bx + c)',
    description: 'Factor simple trinomials where leading coefficient is 1',
    topic: 'Polynomials',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=trinomial%20factoring%20showing%20two%20binomial%20factors%20with%20numbers%20that%20multiply%20and%20add%20correctly%20with%20colorful%20arrows%20on%20white%20background%20educational%20algebra%20illustration&width=400&height=300&seq=g9po5&orientation=landscape'
  },
  {
    id: 'g9-poly-6',
    title: 'Factoring Trinomials (ax² + bx + c)',
    description: 'Factor trinomials with leading coefficients other than 1',
    topic: 'Polynomials',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=complex%20trinomial%20factoring%20with%20ac%20method%20showing%20split%20middle%20term%20and%20grouping%20with%20colorful%20steps%20on%20white%20background%20educational%20algebra%20diagram&width=400&height=300&seq=g9po6&orientation=landscape'
  },
  {
    id: 'g9-poly-7',
    title: 'Factoring Special Products',
    description: 'Factor difference of squares, perfect square trinomials',
    topic: 'Polynomials',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=special%20factoring%20patterns%20showing%20difference%20of%20squares%20and%20perfect%20square%20trinomials%20with%20colorful%20formulas%20on%20white%20background%20educational%20algebra%20poster&width=400&height=300&seq=g9po7&orientation=landscape'
  },

  // Quadratic Equations
  {
    id: 'g9-quad-1',
    title: 'Solving Quadratics by Factoring',
    description: 'Solve quadratic equations using zero product property',
    topic: 'Quadratics',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=quadratic%20equation%20being%20factored%20into%20two%20binomials%20set%20equal%20to%20zero%20with%20solutions%20marked%20with%20colorful%20arrows%20on%20white%20background%20educational%20diagram&width=400&height=300&seq=g9qu1&orientation=landscape'
  },
  {
    id: 'g9-quad-2',
    title: 'Solving Quadratics by Square Root Method',
    description: 'Solve equations of the form x² = k using square roots',
    topic: 'Quadratics',
    difficulty: 'Easy',
    pages: 3,
    thumbnail: 'https://readdy.ai/api/search-image?query=quadratic%20equation%20with%20square%20root%20symbol%20showing%20positive%20and%20negative%20solutions%20with%20colorful%20plus%20minus%20sign%20on%20white%20background%20educational%20illustration&width=400&height=300&seq=g9qu2&orientation=landscape'
  },
  {
    id: 'g9-quad-3',
    title: 'Completing the Square',
    description: 'Solve quadratics by completing the square method',
    topic: 'Quadratics',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=completing%20the%20square%20method%20showing%20perfect%20square%20trinomial%20formation%20with%20geometric%20square%20diagram%20and%20colorful%20steps%20on%20white%20background%20educational%20algebra&width=400&height=300&seq=g9qu3&orientation=landscape'
  },
  {
    id: 'g9-quad-4',
    title: 'Quadratic Formula',
    description: 'Solve any quadratic equation using the quadratic formula',
    topic: 'Quadratics',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=quadratic%20formula%20with%20a%20b%20c%20coefficients%20labeled%20and%20substitution%20steps%20shown%20with%20colorful%20highlighting%20on%20white%20background%20educational%20math%20poster&width=400&height=300&seq=g9qu4&orientation=landscape'
  },
  {
    id: 'g9-quad-5',
    title: 'Discriminant & Nature of Roots',
    description: 'Use discriminant to determine number and type of solutions',
    topic: 'Quadratics',
    difficulty: 'Hard',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=discriminant%20formula%20b%20squared%20minus%204ac%20with%20three%20cases%20showing%20two%20real%20one%20real%20no%20real%20solutions%20with%20colorful%20parabolas%20on%20white%20background%20educational%20diagram&width=400&height=300&seq=g9qu5&orientation=landscape'
  },

  // Statistics
  {
    id: 'g9-stats-1',
    title: 'Mean, Median, Mode, Range',
    description: 'Calculate measures of central tendency and spread',
    topic: 'Statistics',
    difficulty: 'Easy',
    pages: 3,
    thumbnail: 'https://readdy.ai/api/search-image?query=data%20set%20with%20mean%20median%20mode%20range%20calculations%20shown%20with%20colorful%20labels%20and%20number%20line%20on%20white%20background%20educational%20statistics%20illustration&width=400&height=300&seq=g9st1&orientation=landscape'
  },
  {
    id: 'g9-stats-2',
    title: 'Box Plots & Quartiles',
    description: 'Create and interpret box-and-whisker plots',
    topic: 'Statistics',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=box%20and%20whisker%20plot%20showing%20minimum%20Q1%20median%20Q3%20maximum%20with%20colorful%20labels%20and%20number%20line%20on%20white%20background%20educational%20statistics%20diagram&width=400&height=300&seq=g9st2&orientation=landscape'
  },
  {
    id: 'g9-stats-3',
    title: 'Outliers & Interquartile Range (IQR)',
    description: 'Identify outliers using IQR method',
    topic: 'Statistics',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=box%20plot%20with%20outliers%20marked%20as%20dots%20beyond%20whiskers%20showing%20IQR%20calculation%20with%20colorful%20formulas%20on%20white%20background%20educational%20statistics%20illustration&width=400&height=300&seq=g9st3&orientation=landscape'
  },
  {
    id: 'g9-stats-4',
    title: 'Standard Deviation & Variance',
    description: 'Calculate and interpret standard deviation',
    topic: 'Statistics',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=standard%20deviation%20formula%20with%20data%20points%20and%20mean%20showing%20spread%20calculation%20with%20colorful%20bell%20curve%20on%20white%20background%20educational%20statistics%20diagram&width=400&height=300&seq=g9st4&orientation=landscape'
  }
];

const fullReviewPacks = [
  {
    id: 'g9-review-1',
    title: 'Grade 9 Full Review Pack - Part 1',
    description: 'Comprehensive review covering Real Numbers, Linear Equations, and Functions',
    pages: 12,
    thumbnail: 'https://readdy.ai/api/search-image?query=comprehensive%20grade%209%20algebra%20review%20pack%20cover%20with%20multiple%20mathematical%20symbols%20equations%20graphs%20in%20organized%20layout%20on%20white%20background%20educational%20workbook%20style&width=400&height=300&seq=g9rev1&orientation=landscape'
  },
  {
    id: 'g9-review-2',
    title: 'Grade 9 Full Review Pack - Part 2',
    description: 'Comprehensive review covering Systems of Equations and Polynomials',
    pages: 12,
    thumbnail: 'https://readdy.ai/api/search-image?query=algebra%201%20review%20workbook%20cover%20showing%20systems%20of%20equations%20graphs%20and%20polynomial%20expressions%20in%20colorful%20organized%20design%20on%20white%20background%20educational%20style&width=400&height=300&seq=g9rev2&orientation=landscape'
  },
  {
    id: 'g9-review-3',
    title: 'Grade 9 Full Review Pack - Part 3',
    description: 'Comprehensive review covering Quadratic Equations and Statistics',
    pages: 12,
    thumbnail: 'https://readdy.ai/api/search-image?query=quadratic%20equations%20and%20statistics%20review%20pack%20cover%20with%20parabolas%20box%20plots%20formulas%20in%20colorful%20layout%20on%20white%20background%20educational%20workbook%20design&width=400&height=300&seq=g9rev3&orientation=landscape'
  }
];

const topics = [
  'All Topics',
  'Real Numbers',
  'Linear Equations',
  'Functions',
  'Systems',
  'Polynomials',
  'Quadratics',
  'Statistics'
];

const topicColors: Record<string, string> = {
  'Real Numbers': 'bg-blue-100 text-blue-700',
  'Linear Equations': 'bg-green-100 text-green-700',
  'Functions': 'bg-purple-100 text-purple-700',
  'Systems': 'bg-orange-100 text-orange-700',
  'Polynomials': 'bg-pink-100 text-pink-700',
  'Quadratics': 'bg-teal-100 text-teal-700',
  'Statistics': 'bg-indigo-100 text-indigo-700'
};

export default function Grade9WorksheetsPage() {
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredWorksheets = worksheets.filter(ws => {
    const matchesTopic = selectedTopic === 'All Topics' || ws.topic === selectedTopic;
    const matchesSearch = ws.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ws.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTopic && matchesSearch;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-700';
      case 'Medium': return 'bg-yellow-100 text-yellow-700';
      case 'Hard': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Grade 9 Math Worksheets</h1>
              <p className="text-lg text-gray-600">Topic-specific printable drills for Algebra 1 mastery</p>
            </div>
            <Link
              to="/grade/9"
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line text-xl"></i>
              View Curriculum
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link
            to="/grade/9"
            className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-indigo-100 rounded-lg">
              <i className="ri-book-2-line text-2xl text-indigo-600"></i>
            </div>
            <div>
              <div className="font-semibold text-gray-900">Grade 9 Curriculum</div>
              <div className="text-sm text-gray-600">View all topics & lessons</div>
            </div>
          </Link>

          <Link
            to="/grade-9-study-plan"
            className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg">
              <i className="ri-calendar-check-line text-2xl text-green-600"></i>
            </div>
            <div>
              <div className="font-semibold text-gray-900">Study Plan</div>
              <div className="text-sm text-gray-600">Weekly prep schedule</div>
            </div>
          </Link>

          <Link
            to="/grade-9-progress"
            className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-lg">
              <i className="ri-line-chart-line text-2xl text-purple-600"></i>
            </div>
            <div>
              <div className="font-semibold text-gray-900">Progress Tracker</div>
              <div className="text-sm text-gray-600">Track completed worksheets</div>
            </div>
          </Link>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
            <input
              type="text"
              placeholder="Search worksheets by title or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
            />
          </div>
        </div>

        {/* Topic Filter Tabs */}
        <div className="mb-8 bg-white rounded-lg shadow-sm p-2">
          <div className="flex flex-wrap gap-2">
            {topics.map(topic => (
              <button
                key={topic}
                onClick={() => setSelectedTopic(topic)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  selectedTopic === topic
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {topic}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredWorksheets.length}</span> worksheet{filteredWorksheets.length !== 1 ? 's' : ''}
            {selectedTopic !== 'All Topics' && <span> in <span className="font-semibold">{selectedTopic}</span></span>}
          </p>
        </div>

        {/* Worksheets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredWorksheets.map(worksheet => (
            <div key={worksheet.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <div className="h-48 overflow-hidden bg-gray-100">
                <img
                  src={worksheet.thumbnail}
                  alt={worksheet.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${topicColors[worksheet.topic]}`}>
                    {worksheet.topic}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getDifficultyColor(worksheet.difficulty)}`}>
                    {worksheet.difficulty}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{worksheet.title}</h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{worksheet.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <i className="ri-file-text-line"></i>
                    <span>{worksheet.pages} pages</span>
                  </div>
                  <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
                    <i className="ri-download-line"></i>
                    Download
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Full Review Packs Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
              <i className="ri-book-3-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Full Review Packs</h2>
              <p className="text-gray-600">Comprehensive multi-topic review worksheets</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {fullReviewPacks.map(pack => (
              <div key={pack.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                <div className="h-48 overflow-hidden bg-gray-100">
                  <img
                    src={pack.thumbnail}
                    alt={pack.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 whitespace-nowrap">
                      Full Review
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{pack.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{pack.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <i className="ri-file-text-line"></i>
                      <span>{pack.pages} pages</span>
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
                      <i className="ri-download-line"></i>
                      Download
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Empty State */}
        {filteredWorksheets.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-file-search-line text-5xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No worksheets found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => {
                setSelectedTopic('All Topics');
                setSearchQuery('');
              }}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}